import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Enums ---

export const CommentableTypeSchema = z.enum(["TASK"]);

export const CommentAuthorTypeSchema = z.enum(["user", "agent", "system"]);

// --- Attachment schema ---

export const CommentAttachmentSchema = z.object({
  documentId: z.uuid(),
  url: z.string(),
  mimeType: z.string(),
  fileName: z.string(),
});

// --- Response schema ---

export const CommentResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  commentableType: CommentableTypeSchema,
  commentableId: z.uuid(),
  authorType: CommentAuthorTypeSchema,
  authorUserId: z.uuid().nullable(),
  authorAgentDefinitionId: z.uuid().nullable(),
  body: z.string(),
  attachments: z.array(CommentAttachmentSchema).nullable(),
  readAt: z.coerce.date().nullable(),
  readByAgentDefinitionId: z.uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

export const CreateCommentInputSchema = z.object({
  commentableType: CommentableTypeSchema,
  commentableId: z.uuid(),
  body: z.string().min(1).max(50000),
  attachments: z.array(CommentAttachmentSchema).optional(),
});

export const ListCommentsInputSchema = z.object({
  commentableType: CommentableTypeSchema,
  commentableId: z.uuid(),
  limit: z.int().min(1).max(200).prefault(50),
  offset: z.int().min(0).prefault(0),
});

export const ListCommentsResponseSchema = z.object({
  comments: z.array(CommentResponseSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

export const UpdateCommentInputSchema = z.object({
  id: z.uuid(),
  body: z.string().min(1).max(50000),
});

// --- Contracts ---

export const commentsContracts = {
  create: oc
    .input(CreateCommentInputSchema)
    .output(CommentResponseSchema)
    .route({ method: "POST" }),

  list: oc
    .input(ListCommentsInputSchema)
    .output(ListCommentsResponseSchema)
    .route({ method: "GET" }),

  update: oc
    .input(UpdateCommentInputSchema)
    .output(CommentResponseSchema)
    .route({ method: "PUT" }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),
} as const;

// --- Type exports ---

export type CommentableType = z.infer<typeof CommentableTypeSchema>;
export type CommentAuthorType = z.infer<typeof CommentAuthorTypeSchema>;
export type CommentAttachment = z.infer<typeof CommentAttachmentSchema>;
export type CommentResponse = z.infer<typeof CommentResponseSchema>;
export type CreateCommentInput = z.infer<typeof CreateCommentInputSchema>;
export type ListCommentsInput = z.infer<typeof ListCommentsInputSchema>;
export type ListCommentsResponse = z.infer<typeof ListCommentsResponseSchema>;
export type UpdateCommentInput = z.infer<typeof UpdateCommentInputSchema>;
